<template>
	<div class="login-container">
		<v-card max-width="400" :disabled="loginLoading">
			<v-card-text>
				<h1 class="mb-8">Login</h1>

				<v-alert color="error" variant="tonal" density="comfortable" class="mb-5" v-if="loginError">
					{{ loginError }}
				</v-alert>

				<v-form ref="formRef" @submit.prevent="login" validate-on="submit">
					<v-text-field
						ref="emailRef"
						v-model="form.email"
						label="Email"
						color="primary"
						variant="outlined"
						density="comfortable"
						autocomplete="email"
						:rules="[rules.required, rules.validEmail]" />

					<div class="d-flex justify-end">
						<a class="text-caption text-decoration-none text-primary" style="cursor: pointer" @click.prevent="goToForgotPassword">
							Forgot password?
						</a>
					</div>

					<v-text-field
						v-model="form.password"
						label="Password"
						color="primary"
						variant="outlined"
						density="comfortable"
						:append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
						:type="showPassword ? 'text' : 'password'"
						@click:append-inner="togglePassword"
						autocomplete="current-password"
						:rules="[rules.required]"
						class="mb-2" />

					<v-checkbox v-model="rememberMe" label="Remember me" density="comfortable" hide-details class="mb-4" color="primary" />

					<v-btn type="submit" color="primary" class="mb-5" :loading="loginLoading" block> Login </v-btn>
				</v-form>

				<div class="text-center">
					<p>Don't have an account?</p>
					<v-btn color="primary" variant="text" :to="{ name: 'register' }" density="compact"> Register </v-btn>
				</div>
			</v-card-text>
		</v-card>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import formRules from '@/helpers/formRules'

// Setup
const store = useStore()
const router = useRouter()

// State
const form = ref({
	email: '',
	password: '',
})

const showPassword = ref(false)
const rememberMe = ref(false)
const formRef = ref(null)
const emailRef = ref(null)

// Validation Rules
const rules = formRules

// Computed
const loginLoading = computed(() => store.state.auth.loginLoading)
const loginError = computed(() => store.state.auth.loginError)
const authenticated = computed(() => store.getters['auth/authenticated'])

// Methods
const login = async () => {
	const isValid = await formRef.value?.validate()
	if (isValid?.valid && !loginLoading.value) {
		// Handle remember me
		if (rememberMe.value) {
			localStorage.setItem('rememberedEmail', form.value.email)
		} else {
			localStorage.removeItem('rememberedEmail')
		}

		store.dispatch('auth/login', form.value)
	}
}

const togglePassword = () => {
	showPassword.value = !showPassword.value
}

const goToForgotPassword = () => {
	router.push({ name: 'forgotPassword' })
}

// Lifecycle
onMounted(() => {
	// Autofocus email
	setTimeout(() => {
		emailRef.value?.focus()
	}, 100)

	// Load remembered email
	const remembered = localStorage.getItem('rememberedEmail')
	if (remembered) {
		form.value.email = remembered
		rememberMe.value = true
	}
})

// Watchers
watch(loginError, (val) => {
	if (val) {
		setTimeout(() => {
			emailRef.value?.focus()
		}, 100)
	}
})

watch(authenticated, (val) => {
	if (val) {
		router.replace({ name: 'jobs' })
	}
})
</script>

<style scoped>
.main_bg {
	background-image: url('@/assets/pattern-randomized.svg');
	background-size: cover;
}

.login-container {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 50px;
	margin-bottom: 50px;
}

.v-card {
	width: 400px;
	padding: 10px;
}
</style>
